// =============================================================================
// Automation Repository - V3-2
// CRUD pour les automatisations projet
// =============================================================================

import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';

// Types
export type AutomationTriggerType = 
  | 'on_status_change' 
  | 'on_tag_added' 
  | 'on_assignee_added' 
  | 'on_due_date_passed';

export type AutomationActionType = 
  | 'set_owner' 
  | 'add_tag' 
  | 'remove_tag'
  | 'set_priority'
  | 'set_status'
  | 'add_assignee';

export interface AutomationConditions {
  from_status?: string;
  to_status?: string;
  tag_id?: string;
  assignee_id?: string;
}

export interface AutomationAction {
  type: AutomationActionType;
  value: string; // member_id, tag_id, priority value, status_code
}

export interface Automation {
  id: string;
  org_id: string;
  project_id: string;
  name: string;
  description: string | null;
  trigger_type: AutomationTriggerType;
  conditions: AutomationConditions;
  actions: AutomationAction[];
  enabled: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateAutomationInput {
  org_id: string;
  project_id: string;
  name: string;
  description?: string;
  trigger_type: AutomationTriggerType;
  conditions?: AutomationConditions;
  actions: AutomationAction[];
  enabled?: boolean;
  created_by?: string;
}

// =============================================================================
// Fetch Automations
// =============================================================================

export async function fetchAutomations(
  orgId: string,
  projectId: string,
  enabledOnly: boolean = false
): Promise<Automation[]> {
  let query = supabase
    .from('project_automations')
    .select('*')
    .eq('org_id', orgId)
    .eq('project_id', projectId)
    .order('name', { ascending: true });

  if (enabledOnly) {
    query = query.eq('enabled', true);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[automationRepository] fetchAutomations error:', error);
    throw error;
  }

  return (data || []).map(a => ({
    ...a,
    trigger_type: a.trigger_type as AutomationTriggerType,
    conditions: (a.conditions || {}) as unknown as AutomationConditions,
    actions: (a.actions || []) as unknown as AutomationAction[],
  }));
}

// =============================================================================
// Create Automation
// =============================================================================

export async function createAutomation(input: CreateAutomationInput): Promise<Automation> {
  const { data, error } = await supabase
    .from('project_automations')
    .insert({
      org_id: input.org_id,
      project_id: input.project_id,
      name: input.name,
      description: input.description || null,
      trigger_type: input.trigger_type,
      conditions: (input.conditions || {}) as unknown as Json,
      actions: input.actions as unknown as Json,
      enabled: input.enabled ?? true,
      created_by: input.created_by || null,
    })
    .select()
    .single();

  if (error) {
    console.error('[automationRepository] createAutomation error:', error);
    throw error;
  }

  return {
    ...data,
    trigger_type: data.trigger_type as AutomationTriggerType,
    conditions: (data.conditions || {}) as unknown as AutomationConditions,
    actions: (data.actions || []) as unknown as AutomationAction[],
  };
}

// =============================================================================
// Update Automation
// =============================================================================

export async function updateAutomation(
  automationId: string,
  updates: Partial<Pick<Automation, 'name' | 'description' | 'trigger_type' | 'conditions' | 'actions' | 'enabled'>>
): Promise<Automation> {
  const updateData: Record<string, unknown> = {};
  if (updates.name !== undefined) updateData.name = updates.name;
  if (updates.description !== undefined) updateData.description = updates.description;
  if (updates.trigger_type !== undefined) updateData.trigger_type = updates.trigger_type;
  if (updates.conditions !== undefined) updateData.conditions = updates.conditions as unknown as Json;
  if (updates.actions !== undefined) updateData.actions = updates.actions as unknown as Json;
  if (updates.enabled !== undefined) updateData.enabled = updates.enabled;

  const { data, error } = await supabase
    .from('project_automations')
    .update(updateData as any)
    .eq('id', automationId)
    .select()
    .single();

  if (error) {
    console.error('[automationRepository] updateAutomation error:', error);
    throw error;
  }

  return {
    ...data,
    trigger_type: data.trigger_type as AutomationTriggerType,
    conditions: (data.conditions || {}) as unknown as AutomationConditions,
    actions: (data.actions || []) as unknown as AutomationAction[],
  };
}

// =============================================================================
// Toggle Automation
// =============================================================================

export async function toggleAutomation(
  automationId: string,
  enabled: boolean
): Promise<void> {
  const { error } = await supabase
    .from('project_automations')
    .update({ enabled })
    .eq('id', automationId);

  if (error) {
    console.error('[automationRepository] toggleAutomation error:', error);
    throw error;
  }
}

// =============================================================================
// Delete Automation
// =============================================================================

export async function deleteAutomation(automationId: string): Promise<void> {
  const { error } = await supabase
    .from('project_automations')
    .delete()
    .eq('id', automationId);

  if (error) {
    console.error('[automationRepository] deleteAutomation error:', error);
    throw error;
  }
}

// =============================================================================
// Find Matching Automations (for trigger execution)
// =============================================================================

export async function findMatchingAutomations(
  orgId: string,
  projectId: string,
  triggerType: AutomationTriggerType,
  context: {
    from_status?: string;
    to_status?: string;
    tag_id?: string;
    assignee_id?: string;
  }
): Promise<Automation[]> {
  const automations = await fetchAutomations(orgId, projectId, true);
  
  return automations.filter(a => {
    if (a.trigger_type !== triggerType) return false;
    
    const cond = a.conditions;
    
    switch (triggerType) {
      case 'on_status_change':
        if (cond.from_status && cond.from_status !== context.from_status) return false;
        if (cond.to_status && cond.to_status !== context.to_status) return false;
        return true;
        
      case 'on_tag_added':
        if (cond.tag_id && cond.tag_id !== context.tag_id) return false;
        return true;
        
      case 'on_assignee_added':
        if (cond.assignee_id && cond.assignee_id !== context.assignee_id) return false;
        return true;
        
      case 'on_due_date_passed':
        return true;
        
      default:
        return false;
    }
  });
}
